import type { ReactNode } from 'react';
import { Banner, type BannerTone } from './Banner';
import { Card } from './Card';
import { Page } from './Page';
import { Skeleton } from './Skeleton';

interface PageStatusProps {
  title: ReactNode;
  backAction?: ReactNode | undefined;
  fullWidth?: boolean | undefined;
}

/**
 * The whole-page placeholder shown while a record loads.
 *
 * Shaped like the content that is coming rather than a spinner in the middle of
 * an empty card, so the page keeps its geometry and nothing jumps when the data
 * lands. Five of the six detail screens used `<Spinner size="lg">` here, which
 * is the pattern Spinner's own doc comment reserves for atomic blocking actions.
 */
export function PageLoading({
  title,
  backAction,
  fullWidth,
  rows = 6,
}: PageStatusProps & { rows?: number }) {
  return (
    <Page title={title} backAction={backAction} fullWidth={fullWidth}>
      <Card>
        <div className="c-form-grid">
          {Array.from({ length: rows }, (_, index) => (
            <div key={index} className="c-field">
              <Skeleton width="35%" height="0.75rem" />
              <Skeleton height="2.5rem" />
            </div>
          ))}
        </div>
      </Card>
    </Page>
  );
}

/**
 * A whole-page message: a load failure, or a record that is not there.
 *
 * Eight screens wrote this same two-liner, each with its own hand-written "X
 * not found" string.
 */
export function PageMessage({
  title,
  backAction,
  fullWidth,
  tone = 'critical',
  children,
}: PageStatusProps & { tone?: BannerTone; children: ReactNode }) {
  return (
    <Page title={title} backAction={backAction} fullWidth={fullWidth}>
      <Banner tone={tone}>{children}</Banner>
    </Page>
  );
}
